/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PlayIcon,
  EyeIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  XMarkIcon,
  Cog6ToothIcon,
  ClockIcon,
  BoltIcon,
  SignalIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import {
  pipelineService,
  Pipeline,
  PipelineStepDef,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

/** Parse JSON string fields defensively. */
const parseJson = <T,>(raw: any, fallback: T): T => {
  if (!raw) return fallback;
  if (typeof raw === 'object') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const formatDate = (date: string | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString();
};

const StatusBadge: React.FC<{ status: Pipeline['status'] }> = ({ status }) => {
  const cfg: Record<string, string> = {
    active: 'bg-green-500/20 text-green-400',
    archived: 'bg-gray-700 text-gray-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${cfg[status] || cfg.active}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const TriggerIcon: React.FC<{ type: string }> = ({ type }) => {
  const icons: Record<string, React.ElementType> = {
    manual: PlayIcon,
    cron: ClockIcon,
    webhook: BoltIcon,
    on_drift: SignalIcon,
  };
  const Icon = icons[type] || Cog6ToothIcon;
  return <Icon className="w-4 h-4 text-gray-400" />;
};

const StatCard: React.FC<{ title: string; value: number | string; color: string; icon: React.ReactNode }> = ({
  title,
  value,
  icon,
  color,
}) => (
  <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <p className="text-xl font-semibold text-white">{value}</p>
      </div>
    </div>
  </div>
);

/* ── Trigger Run Confirmation Modal ─────────────────────────────────── */
const TriggerRunModal: React.FC<{
  isOpen: boolean;
  pipelineName: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}> = ({ isOpen, pipelineName, loading, onClose, onConfirm }) => {
  const [reason, setReason] = useState('Manual trigger from dashboard');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Trigger Pipeline Run</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          You are about to trigger a new run for <span className="text-white font-medium">{pipelineName}</span>.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Trigger Reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-orange-500"
            placeholder="Reason for triggering..."
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <PlayIcon className="w-4 h-4" />
            {loading ? 'Triggering...' : 'Trigger Run'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────── */
const PipelineList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [triggerModal, setTriggerModal] = useState<{
    isOpen: boolean;
    pipelineId: string;
    pipelineName: string;
  }>({ isOpen: false, pipelineId: '', pipelineName: '' });

  // Fetch pipelines
  const { data: pipelinesData, isLoading } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineService.list({ limit: 200 }),
    select: (res) => res.data,
  });

  const pipelines = pipelinesData?.pipelines || [];
  const total = pipelinesData?.total || 0;

  // Computed stats
  const activePipelines = pipelines.filter((p) => p.status === 'active').length;
  const archivedPipelines = pipelines.filter((p) => p.status === 'archived').length;
  const totalRuns = pipelines.reduce((sum, p) => sum + (p.run_count || 0), 0);

  // Trigger run mutation
  const triggerRunMutation = useMutation({
    mutationFn: ({ pipelineId, reason }: { pipelineId: string; reason: string }) =>
      pipelineService.triggerRun(pipelineId, reason),
    onSuccess: (res) => {
      toast.success('Pipeline run triggered successfully');
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      setTriggerModal({ isOpen: false, pipelineId: '', pipelineName: '' });
      const runId = res.data.run?.id;
      if (runId) {
        navigate(`/registry/pipeline-runs/${runId}`);
      }
    },
    onError: () => {
      toast.error('Failed to trigger pipeline run');
    },
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: (id: string) => pipelineService.archive(id),
    onSuccess: () => {
      toast.success('Pipeline archived');
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    },
    onError: () => {
      toast.error('Failed to archive pipeline');
    },
  });

  const handleTriggerRun = (reason: string) => {
    triggerRunMutation.mutate({
      pipelineId: triggerModal.pipelineId,
      reason,
    });
  };

  const handleArchive = (pipeline: Pipeline) => {
    if (pipeline.status === 'archived') return;
    if (!window.confirm(`Archive pipeline "${pipeline.name}"? This cannot be undone.`)) return;
    archiveMutation.mutate(pipeline.id);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">ML Pipelines</h1>
            <p className="mt-1 text-sm text-gray-400">
              Orchestrate multi-step ML workflows with dependency management
            </p>
          </div>
          <Link
            to="/registry/pipelines/create"
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Create Pipeline
          </Link>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Pipelines"
            value={total}
            icon={<Cog6ToothIcon className="w-5 h-5 text-orange-500" />}
            color="bg-orange-500/20"
          />
          <StatCard
            title="Active"
            value={activePipelines}
            icon={<PlayIcon className="w-5 h-5 text-green-500" />}
            color="bg-green-500/20"
          />
          <StatCard
            title="Archived"
            value={archivedPipelines}
            icon={<ArchiveBoxIcon className="w-5 h-5 text-gray-400" />}
            color="bg-gray-700"
          />
          <StatCard
            title="Total Runs"
            value={totalRuns}
            icon={<ChartBarIcon className="w-5 h-5 text-blue-500" />}
            color="bg-blue-500/20"
          />
        </div>

        {/* Pipeline Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 border-t-orange-500" />
          </div>
        ) : pipelines.length === 0 ? (
          <div className="text-center py-16 bg-gray-900 rounded-xl border border-gray-800">
            <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-600" />
            <h3 className="mt-3 text-sm font-medium text-white">No pipelines yet</h3>
            <p className="mt-1 text-sm text-gray-400">
              Create your first ML pipeline to automate training, evaluation, and deployment workflows.
            </p>
            <Link
              to="/registry/pipelines/create"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Create Pipeline
            </Link>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-800/60">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Steps
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Trigger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Runs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Last Run
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {pipelines.map((pipeline) => {
                    const steps = parseJson<PipelineStepDef[]>(pipeline.steps_json, []);
                    return (
                      <tr key={pipeline.id} className="hover:bg-gray-800/50 transition-colors">
                        <td className="px-6 py-4">
                          <Link
                            to={`/registry/pipelines/${pipeline.id}`}
                            className="text-sm font-medium text-white hover:text-orange-400 transition-colors"
                          >
                            {pipeline.name}
                          </Link>
                          {pipeline.description && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                              {pipeline.description}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-300">{steps.length}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            <TriggerIcon type={pipeline.trigger_type} />
                            <span className="text-sm text-gray-300 capitalize">
                              {pipeline.trigger_type || 'manual'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={pipeline.status} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-300">{pipeline.run_count || 0}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-400">
                            {formatDate(pipeline.last_run_at)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => navigate(`/registry/pipelines/${pipeline.id}`)}
                              className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-700"
                              title="View pipeline"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                setTriggerModal({
                                  isOpen: true,
                                  pipelineId: pipeline.id,
                                  pipelineName: pipeline.name,
                                })
                              }
                              disabled={pipeline.status === 'archived'}
                              className="p-1.5 text-gray-400 hover:text-green-400 rounded hover:bg-green-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Trigger run"
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleArchive(pipeline)}
                              disabled={pipeline.status === 'archived' || archiveMutation.isPending}
                              className="p-1.5 text-gray-400 hover:text-amber-400 rounded hover:bg-amber-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Archive pipeline"
                            >
                              <ArchiveBoxIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trigger Run Modal */}
        <TriggerRunModal
          isOpen={triggerModal.isOpen}
          pipelineName={triggerModal.pipelineName}
          loading={triggerRunMutation.isPending}
          onClose={() => setTriggerModal({ isOpen: false, pipelineId: '', pipelineName: '' })}
          onConfirm={handleTriggerRun}
        />
      </div>
    </div>
  );
};

export default PipelineList;
